import React, { useMemo } from "react";
import {
  useOutletContext,
  useSearchParams,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";

import NavbarScroll from "../../components/subComponents/NavbarScroll";
import { useItemCategoryInfinite } from "../../hooks/useItemCategoryInfinite";
import { ConnectCard1 } from "../../components/subComponents/ConnectCard1";
import { saffronRout, saffronURL } from "../../Url";

const safeArray = (v) => (Array.isArray(v) ? v : []);
const addPosition = (arr) =>
  safeArray(arr).map((item, i) => ({ ...item, position: i }));

const keepKeys = [
  "country",
  "state",
  "city",
  "language",
  "category",
  "category_name",
];

const buildSafeSearch = (searchParams) => {
  const current = new URLSearchParams(searchParams);
  const next = new URLSearchParams();

  keepKeys.forEach((k) => {
    const v = current.get(k);
    if (v) next.set(k, v);
  });

  // ✅ Prev/Next list building ke liye (ConnectDetailsPage)
  next.set("section", "news");

  return `?${next.toString()}`;
};

const News = () => {
  const {
    selectedCountry,
    selectedCategory,
    selectedLanguage,
    selectedState,
    selectedCity,
  } = useOutletContext();

  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const sectionKey = searchParams.get("section"); // ✅ persistent

  // ✅ navbar se aane wala category_name (code)
  const categoryName = searchParams.get("category_name") || "";

  const {
    item: news,
    ref,
    hasNextPage,
    isFetchingNextPage,
    isPending: newsPending,
  } = useItemCategoryInfinite({
    key: "connect_new",
    endpoint: "news",
    listKey: "news",
    category_name: categoryName,
    selectedCategory,
    selectedCountry,
    selectedLanguage,
    selectedState,
    selectedCity,
    enabled: true,
    threshold: 0.7,
    featured: sectionKey === "features" ? 1 : undefined,
  });

  const hasNews = (news?.length || 0) > 0;

  /** ✅ Menu data */
  const { data: menuData, isLoading: loadingTree } = useQuery({
    queryKey: [
      "menuData_connect_slide",
      selectedCountry,
      selectedLanguage,
      selectedCategory,
      selectedState,
      selectedCity,
    ],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (selectedCountry) params.append("country", selectedCountry);
      if (selectedLanguage) params.append("language", selectedLanguage);
      if (selectedCategory) params.append("category", selectedCategory);
      if (selectedState) params.append("state", selectedState);
      if (selectedCity) params.append("city", selectedCity);

      const url = `${saffronURL}/api/v1/public/menu?${params.toString()}`;
      const resp = await axios.get(url);
      return resp.data;
    },
    enabled: !!(selectedCountry || selectedLanguage || selectedCategory),
    retry: 2,
    staleTime: 1000 * 60 * 10,
    cacheTime: 1000 * 60 * 60,
    refetchOnWindowFocus: false,
  });

  const navbaritems = useMemo(() => {
    return menuData?.data?.items?.find((item) => item.key === "news");
  }, [menuData]);

  const getNews = useMemo(() => addPosition(news), [news]);

  /** ✅ Navbar category select */
  const handleCategorySelect = (child) => {
    const params = new URLSearchParams(searchParams);

    if (child?.code) params.set("category_name", child.code);
    else params.delete("category_name");

    setSearchParams(params, { replace: false });
    window.scrollTo(0, 0);
  };

  /** ✅ CARD CLICK => navigate to details route (no local state single page) */
  const handleOpenDetails = (item, idx) => {
    const slug = item?.slug || item?.post_slug || item?.id;
    if (!slug) return;

    navigate(
      {
        pathname: `${saffronRout}/details/news/${slug}`,
        search: buildSafeSearch(searchParams),
      },
      {
        state: {
          sectionKey: "news", // ✅ list fetch key in details page
          position: idx ?? item?.position ?? 0,
        },
      },
    );

    window.scrollTo(0, 0);
  };

  return (
    <>
      <NavbarScroll
        items={navbaritems}
        activeCode={categoryName}
        onSelect={handleCategorySelect}
        loading={loadingTree}
      />

      <section className="py-3  px-1 md:px-5 lg:px-10 ">
        {/* List */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {getNews?.map((item, idx) => (
            <ConnectCard1
              key={item.id}
              item={item}
              onArticleClick={() => handleOpenDetails(item, idx)}
               imgH=" h-[200px]"
              objimg="object-fill"
            />
          ))}
        </div>

        {/* Status */}
        {newsPending && <p className="text-gray-500">Loading articles…</p>}

        {!newsPending && !hasNews && (
          <p className="text-gray-400 italic">
            No articles found in <span className="font-medium">'News'</span>
          </p>
        )}

        {hasNextPage && <div ref={ref} className="h-10" />}

        {isFetchingNextPage && (
          <div className="text-center text-sm text-gray-500 py-4">
            Loading more...
          </div>
        )}
      </section>
    </>
  );
};

export default News;
